/**
 * @description Capitalizes the first word.
 * @param { string } str - The string that will be capitalized.
 * @return { string } - The capitalized word, phrase or text, but, just the first letter.
 */
export function capitalizeFirst(str: string): string {
  let firstLetter = str.charAt(0);
  let remainingLetters = str.substring(1);

  firstLetter = firstLetter.toUpperCase();
  remainingLetters = remainingLetters.toLowerCase();

  return firstLetter + remainingLetters;
}

/**
 * @description Capitalizes the first letter of each word inside param.
 * @param { string } str - The String that will have his words capitalized.
 * @return { string } The capitalized string, with each word capitalized.
 */
export function capitalizeAll(str: string): string {
  const words = str.toLowerCase().split(' ');

  words.forEach((word: string, index: number) => {
    words[index] = capitalizeFirst(word);
  });

  return words.join(' ');
}

/**
 * @description Remove all special characters from string.
 * @param { string } str - The String that will be cleared.
 * @return { string } - The clear string.
 */
export function removeMasks(str: string): string {
  return str.normalize('NFD').replace(/[ ~`!#@$%^&*()+={}\-[\];:'"<>.,\\/?_]/g, '');
}

/**
 * Formats an ISO date string or Date object to the Brazilian date format (dd/MM/yyyy).
 * @param arg - The ISO date string or Date object to be formatted.
 * @returns The formatted date string in the Brazilian date format.
 * @dependencies luxon
 */
export function convertToBrDate(arg: string | null | undefined): string {
  return isNotNullOrUndefined(arg) && isNotEmptyString(arg)
    ? DateTime.fromISO(arg).toFormat('dd/MM/yyyy')
    : '';
}

/**
 * Formats a Brazilian date string to ISO format.
 * @param arg - The date string in the format 'dd/MM/yyyy'.
 * @returns The formatted date string in ISO format.
 * @dependencies luxon
 */
export function convertToIsoDate(arg: string): string {
  return isNotNullOrUndefined(arg) && isNotEmptyString(arg)
    ? DateTime.fromFormat(arg, 'dd/MM/yyyy').toISODate()
    : '';
}

/**
 * Formats a value to Brazilian currency format.
 *
 * @param value - The value to be formatted.
 * @returns The formatted value as a string.
 */
export function formatToBrCurrency(value: number | string): string {
  return !value || (typeof value === 'string' && isEmptyString(value))
    ? '-'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
        .format(Number(value))
        .replace(' ', ' ');
}

export function convertToBrCurrency(value: number | string): string {
  const currency = formatToBrCurrency(value);
  return currency === '-' ? 'R$ 0,00' : currency;
}

export function convertToPercents(value: number | string): string {
  const brPercent = new Intl.NumberFormat('pt-BR', {})
    .format(Number(value))
    .replace(/\u00A0/g, ' ');
  return brPercent === 'NaN' ? '0%' : `${brPercent}%`;
}

/**
 * Formats a Brazilian currency string to a number.
 *
 * @param str - The currency string to be formatted.
 * @returns The formatted number.
 */
export function formatBrCurrencyToNumber(str: string): number {
  return !isEmptyString(str)
    ? Number(str.split('R$')[1].trim().replace(/\./g, '').replace(',', '.'))
    : 0;
}

/**
 * @description Remove all accents from string.
 * @param str
 * @returns The string without accents.
 */
export function removeAccents(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036F]/g, '');
}

/**
 * @description Converts a string to snake case.
 * @param str
 * @returns The string in snake case.
 */
export function toSnakeCase(str: string): string {
  return str.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`).toUpperCase();
}
